import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { CONTROL_BOX } from './form'
import { cx, Spinner } from './primitives'

export interface ComboboxOption {
  value: string
  label: string
  /**
   * Beside the label, in the list only. Whatever else the search matches on belongs here:
   * a row found by its registration number and showing only a name looks like a wrong hit.
   */
  hint?: string
}

export interface ComboboxPage {
  options: ComboboxOption[]
  /** More matched than came back. The list says so rather than passing a part off as the whole. */
  more: boolean
}

export type ComboboxSearch = (query: string, signal: AbortSignal) => Promise<ComboboxPage>

/** One page as it was asked for: which source, which words, and what came back. */
interface Answer {
  search: ComboboxSearch
  query: string
  /** Null when the request failed. */
  page: ComboboxPage | null
}

/** Long enough that a word typed at speed is one request, short enough not to be felt. */
const DEBOUNCE_MS = 200

/**
 * A dropdown that is searched rather than scrolled.
 *
 * <p>
 * For a list too long to read down - customers, where a `<select>` of two hundred names is
 * a list nobody finds anything in. The search runs on the server, not over rows already in
 * the browser: every listing is capped at one page, so filtering what happened to load
 * would answer "no match" for a record that exists, and somebody would raise a duplicate.
 * </p>
 *
 * The selection is passed in whole, label included, because the options come and go with
 * every keystroke and the control still has to say what is chosen when none of them is it.
 *
 * Nothing here clears a selection. A choice is replaced by making another, which is all a
 * required field needs; an optional one would need its own "none" row.
 */
export function Combobox({
  id,
  value,
  onChange,
  search,
  placeholder,
  searchPlaceholder = 'Type to search',
  empty,
  disabled,
}: {
  id?: string
  value: ComboboxOption | null
  onChange: (option: ComboboxOption) => void
  /**
   * Its identity is what the list is a list of. A new function is a new source - another
   * country's customers - and everything loaded for the old one is dropped rather than
   * offered, so wrap it in `useCallback` keyed on whatever scopes it.
   */
  search: ComboboxSearch
  /** While nothing is chosen. */
  placeholder?: string
  /** While the list is open and nothing has been typed. */
  searchPlaceholder?: string
  /** What to say when nothing came back, given what was typed. */
  empty: (query: string) => string
  disabled?: boolean
}) {
  const listId = useId()
  const input = useRef<HTMLInputElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLDivElement>(null)

  const [focused, setFocused] = useState(false)
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [active, setActive] = useState(0)
  const [answer, setAnswer] = useState<Answer>()
  const [attempt, setAttempt] = useState(0)
  const [position, setPosition] = useState<Placement>()

  const query = open ? text.trim() : ''

  // Rows from another source are never drawn, not even dimmed: they are another country's.
  const mine = answer?.search === search ? answer : undefined
  const settled = mine?.query === query
  const failed = settled && mine.page === null
  const options = mine?.page?.options ?? []
  const more = settled && mine.page?.more === true

  // Loads while the field has focus, not while the list is open: the first page is already
  // there by the time somebody who tabbed in presses a key, and every visit starts fresh.
  useEffect(() => {
    if (!focused) return

    const controller = new AbortController()

    const timer = window.setTimeout(
      () => {
        search(query, controller.signal).then(
          (page) => {
            if (!controller.signal.aborted) setAnswer({ search, query, page })
          },
          () => {
            if (!controller.signal.aborted) setAnswer({ search, query, page: null })
          },
        )
      },
      // The unfiltered list is wanted now. A keystroke is probably one of several.
      query === '' ? 0 : DEBOUNCE_MS,
    )

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [focused, query, search, attempt])

  // Opening lands on the current choice, so the list reads as "here is what you have".
  // Once something is typed the top match is the one Enter should take.
  useEffect(() => {
    const chosen = query === '' && value ? options.findIndex((option) => option.value === value.value) : -1

    setActive(Math.max(chosen, 0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, open])

  // Closed, the box holds the chosen name, and a keystroke should replace it rather than be
  // appended to it. Selecting it is what makes the next character the start of a search.
  useEffect(() => {
    if (focused && !open) input.current?.select()
  }, [focused, open, value])

  // Fixed, and measured. The dialog body this usually sits in scrolls and clips, so a list
  // positioned against the field would be cut off at the dialog's edge.
  useLayoutEffect(() => {
    if (!open) return

    function measure() {
      const box = input.current?.getBoundingClientRect()
      if (box) setPosition(place(box, window.innerHeight))
    }

    function onScroll(event: Event) {
      // The list moving under the wheel is not the page moving.
      if (popup.current?.contains(event.target as Node)) return

      measure()
    }

    measure()

    window.addEventListener('resize', measure)
    window.addEventListener('scroll', onScroll, true)

    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open])

  // By hand rather than scrollIntoView, which is free to scroll every ancestor as well.
  useEffect(() => {
    const box = list.current
    const row = box?.children[active]
    if (!open || !box || !(row instanceof HTMLElement)) return

    if (row.offsetTop < box.scrollTop) box.scrollTop = row.offsetTop

    const overshoot = row.offsetTop + row.offsetHeight - (box.scrollTop + box.clientHeight)
    if (overshoot > 0) box.scrollTop += overshoot
  }, [active, open, answer])

  function close() {
    setOpen(false)
    setText('')
  }

  function pick(option: ComboboxOption) {
    onChange(option)
    close()
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()

      if (open) setActive(step(active, event.key === 'ArrowDown' ? 1 : -1, options.length))
      else setOpen(true)

      return
    }

    if (!open) return

    if (event.key === 'Enter') {
      event.preventDefault()

      // Refused until the list answers what is in the box. Typed quickly, Enter arrives
      // before the search does, and the row highlighted at that moment belongs to the
      // previous words - it would put somebody else's name on the quotation.
      const option = settled ? options[active] : undefined
      if (option) pick(option)

      return
    }

    if (event.key === 'Escape') {
      // One Escape, one layer. Left alone it reaches the <dialog> and closes the form
      // around a list that somebody only meant to put away.
      event.preventDefault()
      event.stopPropagation()

      close()
    }
  }

  const note = failed
    ? 'Could not load the list.'
    : !settled
      ? options.length === 0 && 'Searching...'
      : options.length === 0
        ? empty(query)
        : more && `Showing the first ${options.length}. Keep typing to narrow it down.`

  return (
    <div className="relative">
      <input
        ref={input}
        id={id}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={open ? text : (value?.label ?? '')}
        placeholder={open ? (value?.label ?? searchPlaceholder) : placeholder}
        onChange={(event) => {
          setText(event.target.value)
          setOpen(true)
        }}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false)
          close()
        }}
        className={cx(
          CONTROL_BOX,
          'qh-select truncate pr-8',
          // Drawn as the dropdown it replaces, so it reads as a list that opens. The arrow
          // gives way to the spinner rather than sharing the corner with it.
          !(open && !settled) && 'bg-[image:var(--chevron)] bg-[length:16px] bg-[right_0.625rem_center] bg-no-repeat',
        )}
      />

      {open && !settled && (
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted">
          <Spinner className="size-3.5" label={null} />
        </span>
      )}

      {open && position && (
        <div
          ref={popup}
          style={position}
          // A press in here must not move focus: blur is what closes the list, and it would
          // close before the click that was meant to choose from it.
          onMouseDown={(event) => event.preventDefault()}
          className="fixed z-30 flex flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-float"
        >
          <div
            ref={list}
            id={listId}
            role="listbox"
            aria-busy={!settled}
            className={cx(
              'relative min-h-0 overflow-y-auto',
              options.length > 0 && 'p-1',
              // Still there while the next page is on its way, so the list does not collapse
              // and regrow on every keystroke - but visibly not the answer yet.
              !settled && 'opacity-50',
            )}
          >
            {options.map((option, index) => (
              <div
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                // Move, not enter: rows scrolling under a pointer that is lying still would
                // otherwise take the highlight away from the arrow keys.
                onMouseMove={() => {
                  if (index !== active) setActive(index)
                }}
                onClick={() => pick(option)}
                className={cx(
                  'flex cursor-pointer items-center gap-3 rounded px-2.5 py-1.5 text-sm',
                  // The wash rather than the sunken grey a hover gets elsewhere: this row is
                  // where Enter lands, and on the dark surface the grey is all but invisible.
                  index === active ? 'bg-accent-wash text-ink' : 'text-ink-2',
                )}
              >
                <span className="min-w-0 flex-1 truncate">{option.label}</span>
                {option.hint && (
                  <span className="shrink-0 font-mono text-[11.5px] text-muted">{option.hint}</span>
                )}
                {option.value === value?.value && <Tick />}
              </div>
            ))}
          </div>

          {note && (
            <p
              role="status"
              className={cx(
                'px-3.5 py-2.5 text-[12px] text-muted',
                options.length > 0 && 'border-t border-line-soft',
              )}
            >
              {note}
              {failed && (
                <button
                  type="button"
                  // Out of the tab order: focus stays in the box, and typing again retries.
                  tabIndex={-1}
                  onClick={() => {
                    setAnswer(undefined)
                    setAttempt((n) => n + 1)
                  }}
                  className="ml-1.5 font-medium text-accent hover:underline"
                >
                  Try again
                </button>
              )}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export interface Placement {
  left: number
  width: number
  maxHeight: number
  top?: number
  bottom?: number
}

/** Between the field and the list. */
const GAP = 4

/** Kept clear of the edge of the window. */
const EDGE = 8

/** About eight rows. Past that the list is being scrolled, which is what typing is for. */
const TALLEST = 288

/** About four rows - less than this underneath and the list is better off above. */
const ROOM = 160

/**
 * Where the list goes, in viewport coordinates.
 *
 * <p>
 * Below the field, as wide as the field. Above it only when there is not room for a usable
 * list underneath and there is more room on top - a field at the foot of a short window.
 * Above is anchored by `bottom` so a list of two rows still sits against the field instead
 * of floating where a full one would have started.
 * </p>
 *
 * Exported so the arithmetic can be asserted without a layout engine.
 */
export function place(
  anchor: { top: number; bottom: number; left: number; width: number },
  viewportHeight: number,
): Placement {
  const below = viewportHeight - anchor.bottom - GAP - EDGE
  const above = anchor.top - GAP - EDGE
  const across = { left: anchor.left, width: anchor.width }

  return below < ROOM && above > below
    ? { ...across, bottom: viewportHeight - anchor.top + GAP, maxHeight: Math.min(TALLEST, above) }
    : { ...across, top: anchor.bottom + GAP, maxHeight: Math.min(TALLEST, below) }
}

/**
 * The row an arrow key moves to. Wraps, so the last match is one press up from the first.
 *
 * Exported for the same reason as <see cref="place"/>.
 */
export function step(active: number, by: 1 | -1, count: number): number {
  return count === 0 ? 0 : (active + by + count) % count
}

function Tick() {
  return (
    <svg aria-hidden viewBox="0 0 12 12" className="size-3 shrink-0 text-accent">
      <path d="m2.5 6.5 2.5 2.5 4.5-5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
